import { AbstractService } from '@/services/core/serviceFactory';
import { miscGraphqlService, clinicsGraphqlService } from '@/services/api';
import type { GraphQLClientResponse } from '@/services/core/graphqlClient';

export interface Provider {
  provider_id: number;
  provider_name: string;
}

export interface DiagnosisCause {
  id: number;
  cause: string;
}

export interface StatusOption {
  id: number;
  status: string;
}

export interface ProvidersResponse {
  pep_vw_consolidated_data: Provider[];
}

export interface DiagnosisCausesResponse {
  api_prescriptioncauselist: DiagnosisCause[];
}

export interface Clinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
}

export interface AvailableClinicsResponse {
  pep_vw_available_clinics: Clinic[];
}

export class DropdownDataService extends AbstractService {
  /**
   * Fetch providers for a specific clinic
   */
  async fetchProvidersByClinic(clinicId: number): Promise<GraphQLClientResponse<ProvidersResponse>> {
    this.requireAuth();

    if (!clinicId) {
      throw new Error('Clinic ID is required');
    }

    try {
      const providers = await clinicsGraphqlService.getProvidersByClinic(clinicId, this.context.hasuraToken!);
      return {
        data: { pep_vw_consolidated_data: providers },
        errors: null,
        success: true,
        status: 200
      } as unknown as GraphQLClientResponse<ProvidersResponse>;
    } catch (error) {
      throw new Error('Failed to fetch providers');
    }
  }

  /**
   * Fetch diagnosis causes (prescription cause list)
   */
  async fetchDiagnosisCauses(): Promise<GraphQLClientResponse<DiagnosisCausesResponse>> {
    this.requireAuth();

    try {
      const diagnosisCauses = await miscGraphqlService.getDiagnosisCauses(this.context.hasuraToken!);
      return {
        data: { api_prescriptioncauselist: diagnosisCauses },
        errors: null,
        success: true,
        status: 200
      } as GraphQLClientResponse<DiagnosisCausesResponse>;
    } catch (error) {
      throw new Error('Failed to fetch diagnosis causes');
    }
  }

  /**
   * Fetch available clinics
   */
  async fetchAvailableClinics(): Promise<GraphQLClientResponse<AvailableClinicsResponse>> {
    this.requireAuth();

    try {
      const availableClinics = await clinicsGraphqlService.getAvailableClinics(this.context.hasuraToken!);
      return {
        data: { pep_vw_available_clinics: availableClinics },
        errors: null,
        success: true,
        status: 200
      } as GraphQLClientResponse<AvailableClinicsResponse>;
    } catch (error) {
      throw new Error('Failed to fetch available clinics');
    }
  }

  /**
   * Get mock status options
   * This method provides mock data as the original implementation did
   */
  getMockStatusOptions(): StatusOption[] {
    return [
      { id: 1, status: "New" },
      { id: 2, status: "In Progress" },
      { id: 3, status: "Completed" },
      { id: 4, status: "Cancelled" },
      { id: 5, status: "On Hold" },
      { id: 6, status: "Rejected" }
    ];
  }
}